import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@parrot-software/design-system';
import type { ReactElement, ReactNode } from 'react';
import { MoreHorizontal, ExternalLink, Trash2 } from '@parrot-software/design-system/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { cn } from '@parrot-software/utils';

import { VerificationBadge } from '@personnel/presentation/components/shared';
import type { EmployeeDeleteTarget, EmployeeListItem } from '@personnel/presentation/types';

interface EmployeesTableRowProps {
  employee: EmployeeListItem;
  onDelete?: (employee?: EmployeeDeleteTarget) => void;
  checked: boolean;
  onCheckedChange: (employee: EmployeeListItem, checked: boolean) => void;
}

const styles = {
  actionButton: 'size-8',
  icon: 'h-4 w-4 shrink-0',
  cellLeft: 'text-left',
  cellRight: 'text-right',
  roleContent: 'text-foreground no-underline',
  noPaddingX: 'px-0',
  // Link buttons must shrink within their fixed-width cell so the inner text can truncate
  linkButton: 'flex max-w-full min-w-0 items-center gap-1',
  nameText: 'text-sm text-primary font-medium',
  truncate: 'min-w-0 truncate',
  cellText: 'block truncate',
  tooltipContent: 'max-w-md bg-background text-foreground px-3 py-1.5 rounded-md shadow-md text-sm leading-5',
};

/**
 * Renders cell content wrapped in a tooltip that reveals the full value on hover.
 * Used for text columns whose values may be truncated by the fixed table layout.
 *
 * @param {object} props - Component properties
 * @param {ReactNode} props.children - The (single) element acting as the tooltip trigger
 * @param {string} props.label - Full text shown inside the tooltip
 * @returns {ReactElement} Tooltip-wrapped cell content
 */
const TruncatedCell = ({ children, label }: { children: ReactNode; label: string }): ReactElement => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent align="start" className={styles.tooltipContent} side="bottom">
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const EmployeesTableRow = ({
  employee,
  onDelete,
  checked,
  onCheckedChange,
}: EmployeesTableRowProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const history = useHistory();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleDelete = () => {
    if (onDelete) {
      onDelete({ uuid: employee.uuid, roleName: employee.roleName ?? undefined, permissions: employee.permissions });
    }
  };

  const handleEditEmployee = () => {
    history.push(`/employees/${employee.uuid}/edit`);
  };

  const handleEditRoles = () => {
    history.push(`/roles/${employee.roleUuid}`);
  };

  return (
    <TableRow>
      <TableCell className={styles.cellLeft}>
        <Checkbox checked={checked} onCheckedChange={(isChecked) => onCheckedChange(employee, !!isChecked)} />
      </TableCell>
      <TableCell className={styles.cellLeft}>
        <TruncatedCell label={employee.fullName}>
          <Button
            className={cn(styles.linkButton, styles.nameText, styles.noPaddingX)}
            onClick={handleEditEmployee}
            variant="link"
          >
            <span className={styles.truncate}>{employee.fullName}</span>
          </Button>
        </TruncatedCell>
      </TableCell>
      <TableCell className={styles.cellLeft}>
        <TruncatedCell label={employee.email}>
          <span className={styles.cellText}>{employee.email}</span>
        </TruncatedCell>
      </TableCell>
      <TableCell className={styles.cellLeft}>
        {employee.roleName !== 'Super Admin' ? (
          <TruncatedCell label={employee.roleName ?? ''}>
            <Button
              className={cn(styles.roleContent, styles.linkButton, styles.noPaddingX)}
              onClick={handleEditRoles}
              variant="link"
            >
              <span className={styles.truncate}>{employee.roleName}</span>
              <ExternalLink className={styles.icon} />
            </Button>
          </TruncatedCell>
        ) : (
          <TruncatedCell label={employee.roleName ?? ''}>
            <Button
              className={cn(
                styles.roleContent,
                styles.linkButton,
                styles.noPaddingX,
                'no-underline!',
                'cursor-default!',
              )}
              variant="link"
            >
              <span className={styles.truncate}>{employee.roleName}</span>
            </Button>
          </TruncatedCell>
        )}
      </TableCell>
      <TableCell className={styles.cellLeft}>
        <VerificationBadge verificationStatus={employee.verificationStatus} />
      </TableCell>
      <TableCell className={styles.cellRight}>{employee?.assignedStores}</TableCell>
      <TableCell className={cn(styles.cellRight, 'px-2')}>
        <DropdownMenu onOpenChange={setIsMenuOpen} open={isMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button className={styles.actionButton} size="icon" type="button" variant="ghost">
              <MoreHorizontal className={styles.icon} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleDelete}>
              <Trash2 className={styles.icon} /> {t('actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};
